import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../../../redux/api";
import { buildAppLink } from "../../../utils/landingRedirect";

interface StripePrice {
  id: string;
  amount: number | null;
  currency: string;
  interval?: string;
  intervalCount?: number;
}

const PricingSection: React.FC = () => {
  const { t } = useTranslation();
  const [stripePrices, setStripePrices] = useState<{
    plus: { monthly: StripePrice | null; yearly: StripePrice | null };
    pro: { monthly: StripePrice | null; yearly: StripePrice | null };
  }>({
    plus: { monthly: null, yearly: null },
    pro: { monthly: null, yearly: null },
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = await apiRequest({ url: "subscriptions/stripe-prices-public", method: "GET", useAuthHeader: false });
        if (res.tiers) {
          setStripePrices({
            plus: { monthly: res.tiers.plus?.monthly || null, yearly: res.tiers.plus?.yearly || null },
            pro: { monthly: res.tiers.pro?.monthly || null, yearly: res.tiers.pro?.yearly || null },
          });
        }
      } catch { /* fallback */ } finally { setIsLoading(false); }
    })();
  }, []);

  const fmt = (cents: number) => {
    const d = Math.floor(Math.round(cents) / 100);
    const c = Math.round(cents) % 100;
    return c > 0 ? `${d}.${c.toString().padStart(2, "0")}` : `${d}`;
  };

  const currencySymbol = (currency?: string) => currency?.toUpperCase() === "CNY" ? "¥" : "$";

  const plusMonthly = useMemo(() => {
    const p = stripePrices.plus.monthly;
    return p?.amount ? { amount: fmt(p.amount), currency: p.currency.toUpperCase() } : null;
  }, [stripePrices]);

  const proMonthly = useMemo(() => {
    const p = stripePrices.pro.monthly;
    return p?.amount ? { amount: fmt(p.amount), currency: p.currency.toUpperCase() } : null;
  }, [stripePrices]);

  const fallbackCurrency = plusMonthly?.currency || proMonthly?.currency || "USD";
  const fallbackPrice = (tier: "plus" | "pro") => {
    const prices: Record<string, Record<"plus" | "pro", string>> = {
      USD: { plus: "19.99", pro: "49.99" },
      CAD: { plus: "24.99", pro: "59.99" },
      CNY: { plus: "128", pro: "328" },
    };
    return {
      amount: (prices[fallbackCurrency] || prices.USD)[tier],
      currency: fallbackCurrency,
    };
  };

  const plans = [
    {
      id: "plus", name: t("modern.pricing.plus.name", "Plus"), subtitle: t("modern.pricing.plus.subtitle", "Unlock more productivity"),
      price: plusMonthly, fallback: fallbackPrice("plus"),
      features: [t("modern.pricing.plus.f1", "21,000 monthly AI credits"), t("modern.pricing.plus.f2", "More PDF, YouTube, image, and audio imports"), t("modern.pricing.plus.f3", "Daily refresh credits for everyday work"), t("modern.pricing.plus.f4", "Study tools & AI folder chat"), t("modern.pricing.plus.f5", "Export to PDF, DOCX, Markdown"), t("modern.pricing.plus.f6", "Cross-reference across all materials")],
      buttonText: t("modern.pricing.plus.button", "Start free trial"), buttonLink: buildAppLink('/pricing'),
    },
    {
      id: "pro", name: t("modern.pricing.pro.name", "Pro"), subtitle: t("modern.pricing.pro.subtitle", "Best AI for professional work"), popular: true,
      price: proMonthly, fallback: fallbackPrice("pro"),
      features: [t("modern.pricing.pro.f1", "55,000 monthly AI credits"), t("modern.pricing.pro.f2", "Advanced AI models including premium research models"), t("modern.pricing.pro.f3", "Higher recording and import limits"), t("modern.pricing.pro.f4", "PDF graph/table/formula recognition"), t("modern.pricing.pro.f5", "Large PDF auto-split into notes"), t("modern.pricing.pro.f6", "Priority support")],
      buttonText: t("modern.pricing.pro.button", "Start free trial"), buttonLink: buildAppLink('/pricing'),
    },
  ];

  return (
    <motion.section
      className="landing-snap-section flex min-h-screen items-center bg-gray-50 py-16 md:py-20"
      id="pricing"
    >
      <div className="container mx-auto px-4">
        <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-[30px] md:text-[44px] font-bold text-gray-900 mb-4">
            {t("modern.workBrain.pricing.title", "Start with context. Scale into agent workflows.")}
          </h2>
          <p className="text-lg text-gray-500">
            {t("modern.workBrain.pricing.subtitle", "Credits cover capture, source-backed memory, PDF and recording intelligence, chat, drafting, task extraction, and higher-volume automation.")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto items-stretch">
          {plans.map((plan, i) => (
            <motion.div key={plan.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className={`relative rounded-2xl bg-white flex flex-col ${
                plan.popular ? "border-2 border-cornflowerblue shadow-xl lg:scale-[1.03]" : "border border-gray-200 shadow-md"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-6">
                  <span className="bg-blue-50 text-cornflowerblue text-[11px] font-bold uppercase px-3 py-1 rounded-md">
                    {t("modern.workBrain.pricing.popular", "Most Popular")}
                  </span>
                </div>
              )}

              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{plan.name}</h3>
                <p className="text-sm text-gray-500 mt-1 mb-5">{plan.subtitle}</p>

                {/* Price */}
                <div className="mb-5 h-[56px] flex items-end">
                  {plan.price ? (
                    <div className="flex items-start">
                      <span className={`text-lg font-bold mt-1 mr-0.5 ${plan.id === "free" ? "text-green-500" : "text-gray-900"}`}>{currencySymbol(plan.price.currency)}</span>
                      <span className={`text-[42px] font-bold leading-none ${plan.id === "free" ? "text-green-500" : "text-gray-900"}`}>{plan.price.amount}</span>
                      <div className="ml-1.5 mt-2">
                        <span className="text-[11px] font-bold text-gray-400 block">{plan.price.currency}</span>
                        <span className="text-[11px] font-medium text-gray-400">/ month</span>
                      </div>
                    </div>
                  ) : plan.fallback ? (
                    <div className="flex items-start">
                      <span className="text-lg font-bold text-gray-900 mt-1 mr-0.5">{currencySymbol(plan.fallback.currency)}</span>
                      <span className="text-[42px] font-bold text-gray-900 leading-none">{plan.fallback.amount}</span>
                      <div className="ml-1.5 mt-2">
                        <span className="text-[11px] font-bold text-gray-400 block">{plan.fallback.currency}</span>
                        <span className="text-[11px] font-medium text-gray-400">/ month</span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-3xl font-bold text-gray-900">Custom</span>
                  )}
                </div>

                {/* CTA */}
                {plan.buttonLink ? (
                  <a href={plan.buttonLink} target={plan.buttonLink.startsWith("mailto") ? undefined : "_blank"} rel="noopener noreferrer"
                    className={`w-full py-3 rounded-xl text-sm font-semibold text-center block no-underline transition-colors mb-5 ${
                      plan.buttonText === "Contact Sales"
                        ? "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        : "bg-gray-900 text-white hover:bg-gray-800"
                    }`}>
                    {plan.buttonText}
                  </a>
                ) : (
                  <button disabled className="w-full py-3 rounded-xl bg-gray-200 text-gray-400 text-sm font-semibold cursor-not-allowed mb-5">
                    {plan.buttonText}
                  </button>
                )}

                <div className="border-t border-gray-100 mb-5" />

                {/* Features */}
                <ul className="space-y-3 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Icon icon="lucide:check" className="text-green-500 mt-0.5 flex-shrink-0" width={16} />
                      <span className={`text-[13px] text-gray-600 leading-relaxed ${j === 0 && plan.id === "pro" ? "font-semibold" : ""}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer intentionally blank */}
      </div>
    </motion.section>
  );
};

export default PricingSection;
